import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  buildSessionDocumentViews,
  canExposeRecords,
  computeCareContextProgress,
  createExactReferenceIndex,
  getBundleTypeLabel,
  getBundleVisitCaption,
  getCompositionSummary,
  getEncounterInfoFields,
  getPatientInfoFields,
  getPractitionerInfoFields,
  resolveCompositionLinkedResource,
  resolveDiagnosticReportRelations,
  summarizeResourceCard,
  type AttachmentRef,
  type BundleDocumentView,
  type ConsentSessionLike,
} from './fhirDocumentViewModel';
import { getConsentAttachment, getConsentErrorMessage } from '../../custom/services/abdmConsentService';

type Props = {
  session: ConsentSessionLike & { _id?: string };
  notify?: (opts: { title: string; message: string; type: 'success' | 'error' | 'info' }) => void;
};

type PreviewState = {
  url: string;
  contentType: string;
  title: string;
};

function triggerDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName || 'document';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function useAttachmentPreview(notify?: Props['notify']) {
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewState | null>(null);

  const closePreview = () => {
    setPreview(current => {
      if (current?.url) URL.revokeObjectURL(current.url);
      return null;
    });
  };

  useEffect(() => {
    if (!preview) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closePreview();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [preview]);

  useEffect(
    () => () => {
      if (preview?.url) URL.revokeObjectURL(preview.url);
    },
    [preview]
  );

  const openAttachment = async (
    ref: AttachmentRef,
    fallbackSessionId: string,
    options?: { download?: boolean }
  ) => {
    const bundleId = String(ref.bundleId || '');
    const num = ref.num ?? 1;
    const title = typeof ref.title === 'string' ? ref.title : `Attachment ${num}`;
    const key = String(ref.refId || ref.fullUrl || `${bundleId}-${num}`);
    if (!bundleId) {
      notify?.({ title: 'Attachment', message: 'Missing bundle id for this document', type: 'error' });
      return;
    }

    setLoadingKey(key);
    try {
      const attachment = await getConsentAttachment(
        String(ref.sessionId || fallbackSessionId),
        bundleId,
        num
      );
      if (!attachment?.content) {
        notify?.({ title: 'Attachment', message: 'No content returned', type: 'error' });
        return;
      }

      // Guard against huge base64 payloads freezing the tab (~25MB binary).
      const MAX_ATTACHMENT_BASE64_CHARS = 35_000_000;
      if (String(attachment.content).length > MAX_ATTACHMENT_BASE64_CHARS) {
        notify?.({
          title: 'Attachment too large',
          message: 'This document is too large to open in the browser.',
          type: 'error',
        });
        return;
      }

      const contentType = attachment.contentType || 'application/pdf';
      const byteCharacters = atob(attachment.content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: contentType });
      const fileName = `${title.replace(/[^\w.\- ]+/g, '_').trim() || 'document'}${
        contentType.includes('pdf') ? '.pdf' : ''
      }`;

      if (options?.download || !contentType.includes('pdf')) {
        triggerDownload(blob, fileName);
        if (!contentType.includes('pdf')) return;
      }

      if (contentType.includes('pdf') || contentType.startsWith('image/')) {
        setPreview({
          url: URL.createObjectURL(blob),
          contentType,
          title,
        });
      } else {
        triggerDownload(blob, fileName);
      }
    } catch (err) {
      notify?.({
        title: 'Attachment failed',
        message: getConsentErrorMessage(err),
        type: 'error',
      });
    } finally {
      setLoadingKey(null);
    }
  };

  const previewUi = preview ? (
    <div className="abha-attachment-preview-backdrop" role="presentation" onClick={closePreview}>
      <section
        className="abha-attachment-preview"
        role="dialog"
        aria-modal="true"
        aria-label={preview.title}
        onClick={event => event.stopPropagation()}
      >
        <header className="abha-attachment-preview__header">
          <h3>{preview.title}</h3>
          <div className="abha-attachment-preview__actions">
            <a
              className="abha-consent-btn abha-consent-btn--ghost"
              href={preview.url}
              download={`${preview.title.replace(/[^\w.\- ]+/g, '_').trim() || 'document'}.pdf`}
            >
              Download
            </a>
            <button type="button" className="abha-consent-btn abha-consent-btn--ghost" onClick={closePreview}>
              Close
            </button>
          </div>
        </header>
        <div className="abha-attachment-preview__body">
          {preview.contentType.startsWith('image/') ? (
            <img src={preview.url} alt={preview.title} />
          ) : (
            <iframe src={preview.url} title={preview.title} />
          )}
        </div>
      </section>
    </div>
  ) : null;

  return { loadingKey, openAttachment, previewUi };
}

function InfoGrid({
  title,
  subtitle,
  fields,
}: {
  title: string;
  subtitle?: string;
  fields: Array<{ label: string; value: string }>;
}) {
  if (!fields.length) return null;
  return (
    <section className="abha-info-section">
      <h5>{title}</h5>
      <div className="abha-info-card">
        {subtitle ? <h6>{subtitle}</h6> : null}
        <dl className="abha-info-grid">
          {fields.map(field => (
            <div key={`${field.label}-${field.value}`}>
              <dt>{field.label}</dt>
              <dd>{field.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

function DocumentReferenceCard({
  title,
  loading,
  onOpen,
}: {
  title?: string;
  loading?: boolean;
  onOpen: () => void;
}) {
  return (
    <button type="button" className="abha-docref-card" onClick={onOpen} disabled={loading}>
      <span className="abha-docref-card__icon" aria-hidden="true">
        ↓
      </span>
      <span className="abha-docref-card__body">
        <span className="abha-docref-card__source">Source: {title || 'Document'}</span>
        <span className="abha-docref-card__hint">
          {loading ? 'Opening…' : 'Click to view/download from the attachment list above.'}
        </span>
      </span>
    </button>
  );
}

function ResourceFieldsCard({
  title,
  resource,
}: {
  title: string;
  resource: Record<string, unknown>;
}) {
  const summary = summarizeResourceCard(resource);
  const fields = summary.fields;
  return (
    <div className="abha-info-card">
      <h6>{title || summary.title}</h6>
      {fields.length ? (
        <dl className="abha-info-grid">
          {fields.map(field => (
            <div key={field.label}>
              <dt>{field.label}</dt>
              <dd>{field.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </div>
  );
}

function DiagnosticReportCard({
  report,
  index,
}: {
  report: Record<string, unknown>;
  index: Map<string, Record<string, unknown>>;
}) {
  const processedType = String(report.processedType || report.sourceResourceType || 'DiagnosticReport');
  const summary = summarizeResourceCard(report);
  const { observations, specimens, unresolved } = resolveDiagnosticReportRelations(report, index);

  return (
    <div className="abha-info-card abha-diagnostic-report">
      <h6>{processedType}</h6>
      {summary.fields.length ? (
        <dl className="abha-info-grid">
          {summary.fields.map(field => (
            <div key={field.label}>
              <dt>{field.label}</dt>
              <dd>{field.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {observations.map((obs, i) => (
        <ResourceFieldsCard
          key={`obs-${String(obs.id || obs.fullUrl || i)}`}
          title={`Observation: ${summarizeResourceCard(obs).title}`}
          resource={obs}
        />
      ))}

      {specimens.map((specimen, i) => (
        <ResourceFieldsCard
          key={`spec-${String(specimen.id || specimen.fullUrl || i)}`}
          title={`Specimen: ${summarizeResourceCard(specimen).title}`}
          resource={specimen}
        />
      ))}

      {unresolved.map(ref => (
        <p key={ref} className="abha-fhir-incomplete">
          Incomplete: could not resolve DiagnosticReport reference <code>{ref}</code>
        </p>
      ))}
    </div>
  );
}

function findAttachmentForEntry(
  entryResource: Record<string, unknown>,
  attachmentRefs: AttachmentRef[]
): AttachmentRef | undefined {
  const fullUrl = typeof entryResource.fullUrl === 'string' ? entryResource.fullUrl : '';
  const refId = typeof entryResource.refId === 'string' ? entryResource.refId : '';

  // Prefer exact identifiers only — never match by title (duplicate titles = PHI mix-up).
  return (
    attachmentRefs.find(ref => Boolean(fullUrl) && ref.fullUrl === fullUrl) ||
    attachmentRefs.find(ref => Boolean(refId) && ref.refId === refId)
  );
}

function CareContextAccordion({
  view,
  session,
  sessionId,
  notify,
  defaultOpen = false,
}: {
  view: BundleDocumentView;
  session: ConsentSessionLike;
  sessionId: string;
  notify?: Props['notify'];
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const attachmentButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const { loadingKey, openAttachment, previewUi } = useAttachmentPreview(notify);
  const bundle = view.meta.bundle;
  const title = view.compositionTitle || `Record ${view.meta.bundleIndex + 1}`;
  const caption = getBundleVisitCaption(bundle);
  const bundleId = String(bundle.id || view.attachmentRefs[0]?.bundleId || '');
  const typeLabel = getBundleTypeLabel(bundle);
  const summary = getCompositionSummary(view.meta);
  const patient = resolveCompositionLinkedResource(view.meta, 'subject');
  const practitioner = resolveCompositionLinkedResource(view.meta, 'author');
  const encounter = resolveCompositionLinkedResource(view.meta, 'encounter');
  const patientFields = getPatientInfoFields(patient, session);
  const practitionerFields = getPractitionerInfoFields(practitioner);
  const encounterFields = getEncounterInfoFields(encounter);
  const usableAttachments = view.attachmentRefs.filter(ref => Boolean(ref.bundleId));
  const referenceIndex = useMemo(() => createExactReferenceIndex(bundle), [bundle]);

  const openAndHighlight = async (ref: AttachmentRef) => {
    const key = String(ref.refId || ref.fullUrl || `${ref.bundleId}-${ref.num}`);
    const button = attachmentButtonRefs.current[key];
    if (button) {
      button.scrollIntoView({ behavior: 'smooth', block: 'center' });
      button.classList.add('is-highlighted');
      window.setTimeout(() => button.classList.remove('is-highlighted'), 1600);
    }
    await openAttachment(ref, sessionId);
  };

  return (
    <article className={`abha-accordion ${open ? 'is-open' : ''}`}>
      <button
        type="button"
        className="abha-accordion__summary"
        aria-expanded={open}
        onClick={() => setOpen(value => !value)}
      >
        <div className="abha-accordion__main">
          <div className="abha-accordion__title-row">
            <span className="abha-accordion__title-label">Title:</span>
            <strong>{title}</strong>
          </div>
          <p className="abha-accordion__caption">{caption}</p>
        </div>
        <div className="abha-accordion__meta">
          {bundleId ? <span className="abha-accordion__id">ID: {bundleId}</span> : null}
          {view.meta.hipName ? <span className="abha-chip abha-chip--success">{view.meta.hipName}</span> : null}
          <span className="abha-chip abha-chip--primary">
            {typeLabel}
            <span className="abha-accordion__chevron" aria-hidden="true" />
          </span>
        </div>
      </button>

      {open ? (
        <div className="abha-accordion__details">
          <div className="abha-composition-summary">
            {summary.status ? (
              <div>
                <span>Status</span>
                <strong>{summary.status}</strong>
              </div>
            ) : null}
            {summary.dateMarked ? (
              <div>
                <span>Date Marked on Composition</span>
                <strong>{summary.dateMarked}</strong>
              </div>
            ) : null}
            {summary.custodian ? (
              <div>
                <span>Custodian</span>
                <strong>{summary.custodian}</strong>
              </div>
            ) : null}
            {summary.visitDate ? (
              <div>
                <span>Visit Date</span>
                <strong>{summary.visitDate}</strong>
              </div>
            ) : null}
          </div>

          {usableAttachments.length ? (
            <section className="abha-info-section">
              <h5>Attachments</h5>
              <div className="abha-fhir-attachments">
                {usableAttachments.map((ref, i) => {
                  const key = String(ref.refId || ref.fullUrl || `${ref.bundleId}-${ref.num ?? i}`);
                  const isLoading = loadingKey === key;
                  return (
                    <button
                      key={key}
                      ref={element => {
                        attachmentButtonRefs.current[key] = element;
                      }}
                      type="button"
                      className="abha-attach-btn"
                      onClick={() => void openAndHighlight(ref)}
                      disabled={isLoading}
                    >
                      {isLoading
                        ? 'Opening…'
                        : `VIEW ${(typeof ref.title === 'string' ? ref.title : `Attachment ${ref.num ?? i}`).toUpperCase()}`}
                    </button>
                  );
                })}
              </div>
            </section>
          ) : null}

          <InfoGrid title="Subject" subtitle="Patient Info" fields={patientFields} />
          <InfoGrid title="Authors" subtitle="Practitioner Info" fields={practitionerFields} />
          <InfoGrid title="Encounter" subtitle="Encounter Info" fields={encounterFields} />

          {view.sections.map((section, sIdx) => (
            <section key={`${section.title}-${sIdx}`} className="abha-info-section">
              <h5>{section.title}</h5>
              {section.entries.map((entry, eIdx) => {
                if (entry.unresolved || !entry.resource) {
                  return (
                    <p key={eIdx} className="abha-fhir-incomplete">
                      Incomplete: could not resolve reference <code>{entry.reference || '(empty)'}</code>
                    </p>
                  );
                }
                const processedType = String(
                  entry.resource.processedType || entry.resource.sourceResourceType || ''
                );
                if (
                  processedType.includes('DocumentReference') ||
                  processedType === 'AttachmentRef' ||
                  section.title.toLowerCase().includes('document reference')
                ) {
                  const matched =
                    findAttachmentForEntry(entry.resource, usableAttachments) ||
                    findAttachmentForEntry(
                      { fullUrl: entry.reference },
                      usableAttachments
                    );
                  const matchedKey = matched
                    ? String(matched.refId || matched.fullUrl || `${matched.bundleId}-${matched.num}`)
                    : '';
                  return (
                    <DocumentReferenceCard
                      key={eIdx}
                      title={
                        typeof matched?.title === 'string'
                          ? matched.title
                          : typeof entry.resource.title === 'string'
                            ? entry.resource.title
                            : view.compositionTitle
                      }
                      loading={Boolean(matchedKey && loadingKey === matchedKey)}
                      onOpen={() => {
                        if (!matched) {
                          notify?.({
                            title: 'Attachment',
                            message: 'No attachment found for this document reference',
                            type: 'error',
                          });
                          return;
                        }
                        void openAndHighlight(matched);
                      }}
                    />
                  );
                }
                if (processedType.includes('DiagnosticReport')) {
                  return (
                    <DiagnosticReportCard
                      key={eIdx}
                      report={entry.resource}
                      index={referenceIndex}
                    />
                  );
                }
                return (
                  <ResourceFieldsCard
                    key={eIdx}
                    title={processedType || section.title}
                    resource={entry.resource}
                  />
                );
              })}
            </section>
          ))}
          {previewUi}
        </div>
      ) : null}
    </article>
  );
}

export default function FhirDocumentPanel({ session, notify }: Props) {
  const views = useMemo(() => buildSessionDocumentViews(session), [session]);
  const progress = useMemo(() => computeCareContextProgress(session), [session]);

  if (!canExposeRecords(session)) {
    const status = String(session.status || session.sessionStatus || 'unknown').toUpperCase();
    const blocked = ['DENIED', 'REVOKED', 'EXPIRED'].includes(status);
    return (
      <div className="abha-fhir-blocked">
        {blocked ? (
          <>
            Records are not available for status <strong>{status}</strong>. Denied, revoked, and expired
            consents do not expose health information.
          </>
        ) : (
          <>
            No granted health records to display yet (status: <strong>{status}</strong>).
          </>
        )}
      </div>
    );
  }

  if (!views.length) {
    return <div className="abha-fhir-empty">No health records received for this consent yet.</div>;
  }

  const sessionId = String(session._id || '');
  const receivedPct = progress.total > 0 ? (progress.received / progress.total) * 100 : 0;
  const pendingPct = progress.total > 0 ? (progress.pending / progress.total) * 100 : 0;

  return (
    <div className="abha-fhir-panel">
      <div className="abha-care-progress">
        <h3>Status of Care Context Data Received</h3>
        <div className="abha-care-progress__stats">
          <span>
            {progress.received} of {progress.total} received
          </span>
          <span>
            Pending: {progress.pending}
            <span className="abha-care-progress__sep">N/A: {progress.invalid}</span>
          </span>
        </div>
        <div className="abha-care-progress__bar" aria-hidden="true">
          <span className="abha-care-progress__received" style={{ width: `${receivedPct}%` }} />
          <span
            className="abha-care-progress__pending"
            style={{ left: `${receivedPct}%`, width: `${pendingPct}%` }}
          />
        </div>
      </div>

      <div className="abha-accordion-list">
        {views.map((view, index) => (
          <CareContextAccordion
            key={String(view.meta.bundle.id || view.meta.bundleIndex)}
            view={view}
            session={session}
            sessionId={sessionId}
            notify={notify}
            defaultOpen={false}
          />
        ))}
      </div>
    </div>
  );
}
